"use client";
import { useCallback, useEffect, useState } from "react";
import { useScopedI18n } from "@/messages/client";
import Title from "@/components/ui/title";
import { DirectusImage } from "@/components/DirectusImage";
import RouterLink from "@/components/ui/router-link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./index.css";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";


export default function CommonNewProductClient(props: {
  data?: {
    slug?: string;
    card_cover?: string;
    name?: string;
    summary?: string;
  }[]
  titleSize?: "lg" | "md" | "sm";
}) {  

  const { titleSize = "lg" } = props;
  const t = useScopedI18n("产品");
  const tCommon = useScopedI18n("通用");
  const [current, setCurrent] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);


  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  return (
    <div className="w-full max-md:w-[100vw] max-md:-ml-6 overflow-hidden">
      {titleSize === "lg" && <Title title={t("新品推荐")} />}
      {titleSize === "md" && <Title title={t("新品推荐")} underline={false} />}
      {titleSize === "sm" && <Title title={t("新品推荐")} className="md:text-left md:items-start md:text-4xl" underline={false} />}
      <div className="w-full relative">
        <Carousel
          className="relative mt-6 w-full md:box-content md:mt-12 md:-ml-4 md:px-4"
          opts={{
            loop: true,
            breakpoints: {
              "(min-width:768px)": {
                align: "start",
              }
            }
          }}
          setApi={setMainApi}
        >
          <CarouselContent className="m-0 py-2">
            {props.data?.map((el, index) => (
              <CarouselItem
                key={index}
                className="px-2 basis-[68vw] md:basis-1/2 lg:basis-1/3"
              >
                <RouterLink
                  href={el.slug ? `/product/${el.slug}` : undefined}
                  className={cn(
                    "relative w-full h-[296px] cursor-pointer rounded-lg group",
                    "md:rounded-md md:border md:border-zinc-200 md:group md:bg-zinc-50 md:hover-outline-shadow md:h-[386px]",
                    {
                      "max-md:opacity-50": current !== index,
                      "max-md:shadow-md": current === index,
                      "max-md:border max-md:border-zinc-200": current !== index,
                    }
                  )}
                >
                  <img
                    className="absolute top-0 right-0 w-7 z-10 md:w-14"
                    src="/icon/new.png" alt="Icon New Product"
                  />
                  <div className={cn(
                    "flex items-center justify-center overflow-hidden h-[144px] rounded-t-lg bg-cover bg-[url(/product-bg.png)]",
                    "md:h-[218px] md:rounded-t-md"
                  )}>
                    <DirectusImage
                      id={el.card_cover || ""}
                      alt={el.name || ""}
                      zoomInGroup
                      width={284}
                      height={156}
                      className="object-cover"
                    />
                  </div>
                  <div className="px-8 pt-4 max-md:flex max-md:flex-col max-md:px-4 max-md:pt-5 max-md:h-[152px] max-md:bg-white max-md:rounded-b-lg">
                    <p className="text-lg font-bold max-md:overflow-hidden max-md:text-ellipsis">{el.name}</p>
                    <p className="text-ellipsis line-clamp-2 text-gray text-sm mt-2 max-md:h-10">{el.summary}</p>
                    <p className="text-gray text-sm mt-2 max-md:self-end md:text-right md:text-xs">
                      {tCommon("前往了解更多")}
                      <ChevronRight className="inline ml-1" size={14} />
                    </p>
                  </div>
                </RouterLink>
              </CarouselItem>
            ))}
          </CarouselContent>
          <span
            className={cn(
              "new-product-prev max-md:!hidden",
              {
                "hidden": !mainApi?.canScrollPrev(),
              }
            )}
            onClick={() => {
              mainApi?.scrollPrev();
            }}
          ><ChevronLeft /></span>
          <span
            className={cn(
              "new-product-next max-md:!hidden",
              {
                "hidden": !mainApi?.canScrollNext(),
              }
            )}
            onClick={() => {
              mainApi?.scrollNext();
            }}
          ><ChevronRight /></span>
        </Carousel>
        <div className="mt-4 flex w-full items-center justify-center space-x-2 md:hidden">
          {scrollSnaps.map((_, index) => (
            <div
              key={index}
              className={cn(
                "h-1.5 w-1.5 rounded-full bg-[#CCCCCC] transition-all duration-300 ease-in-out",
                {
                  "w-4 bg-[#2C2C2C]": current === index,
                }
              )}></div>
          ))}
        </div>
      </div>
    </div>
  )
}
